import * as Api from '@ViewModels';
import { useCallback } from 'react';
import {
	useAutomationTemplateStepSwitchAddCaseMutation,
	useAutomationTemplateStepSwitchRemoveCaseMutation,
	useAutomationTemplateStepSwitchUpdateCaseMutation,
} from '../../../../queries/Automation/mutations/switchCases';
import { useAddAutomationStepMutation } from '../../../../queries/Automation/mutations/steps/useAddAutomationStepMutation';

interface UseSegmentActionsParams {
	automationTemplateId: string;
	trigger: Api.IAutomationTrigger;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Custom hook for managing switch step segments and cases in automation templates.
 * Provides methods to add segments (switch steps), add/remove/update cases within segments.
 *
 * @param automationTemplateId - The ID of the automation template
 * @param trigger - The automation trigger that determines default filter criteria
 * @param impersonationContext - Optional impersonation context for API calls
 * @returns Object containing segment management functions and loading state
 */
export function useSegmentActions({ automationTemplateId, trigger, impersonationContext }: UseSegmentActionsParams) {
	const addStepMutation = useAddAutomationStepMutation();
	const addCaseMutation = useAutomationTemplateStepSwitchAddCaseMutation();
	const removeCaseMutation = useAutomationTemplateStepSwitchRemoveCaseMutation();
	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation();

	const isBusy =
		addStepMutation.isPending ||
		addCaseMutation.isPending ||
		removeCaseMutation.isPending ||
		updateCaseMutation.isPending;

	/**
	 * Gets the default filter criteria based on the trigger type.
	 * Uses Policy filter for PolicyRenew triggers, otherwise uses Tag filter.
	 *
	 * @returns Default contact filter criteria
	 */
	const getDefaultFilter = useCallback((): Api.IContactFilterCriteria => {
		const isPolicyRenewTrigger = Api.VmUtils.Automations.triggers.isResourceSelectorTrigger(
			trigger,
			Api.ResourceSelectorId.PolicyRenew
		);

		return {
			criteria: [
				{
					property: isPolicyRenewTrigger
						? Api.ContactFilterCriteriaProperty.Policy
						: Api.ContactFilterCriteriaProperty.Tag,
				},
			],
		};
	}, [trigger]);

	/**
	 * Adds a case to an existing switch step.
	 *
	 * @param switchStepId - The ID of the switch step to add the case to
	 * @param filter - Optional filter criteria. If not provided, uses default filter based on trigger
	 * @param isDefault - Whether this is a default case (default cases have null filter)
	 * @returns Promise that resolves to the updated switch step
	 */
	const addCaseToSwitch = useCallback(
		async (switchStepId: string, filter?: Api.IContactFilterCriteria, isDefault = false) => {
			const caseData: Api.IAutomationStepCaseStatement = {
				filter: isDefault ? null : filter || getDefaultFilter(),
				isDefault,
			};

			return addCaseMutation.mutateAsync({
				templateId: automationTemplateId,
				stepId: switchStepId,
				caseData,
				impersonationContext,
			});
		},
		[automationTemplateId, impersonationContext, getDefaultFilter, addCaseMutation]
	);

	/**
	 * Adds a new segment (switch step) to the automation template.
	 * If a switch step already exists, adds an empty case to it.
	 * If no switch step exists, creates one with an empty case and a default case.
	 *
	 * @param existingSwitchStep - Optional existing switch step to add a case to
	 * @returns Promise that resolves to the switch step (either existing or newly created)
	 */
	const addSegment = useCallback(
		async (existingSwitchStep?: Api.ISwitchAutomationStep) => {
			if (existingSwitchStep?.id) {
				await addCaseToSwitch(existingSwitchStep.id);
				return existingSwitchStep;
			}

			const newSwitchStep = await addStepMutation.mutateAsync({
				templateId: automationTemplateId,
				stepType: Api.AutomationStepType.Switch,
				step: {
					_type: 'SwitchAutomationStep',
				} as Api.ISwitchAutomationStep,
				impersonationContext,
			});

			// Add small delay to allow backend to finish persisting the switch step
			await new Promise(resolve => setTimeout(resolve, 100));

			// Helper function to add case with retry on 405
			const addCaseWithRetry = async (isDefault: boolean, retries = 1) => {
				try {
					return await addCaseToSwitch(newSwitchStep.id, undefined, isDefault);
				} catch (error: any) {
					// Retry once on 405 errors after additional delay
					if (retries > 0 && error?.status === 405) {
						await new Promise(resolve => setTimeout(resolve, 500));
						return addCaseWithRetry(isDefault, retries - 1);
					}
					throw error;
				}
			};

			await addCaseWithRetry(false); // Empty case
			await addCaseWithRetry(true); // Default case
			return newSwitchStep as Api.ISwitchAutomationStep;
		},
		[automationTemplateId, impersonationContext, addCaseToSwitch, addStepMutation]
	);

	/**
	 * Removes a case from a switch step.
	 *
	 * @param switchStepId - The ID of the switch step containing the case
	 * @param caseId - The ID of the case to remove
	 * @returns Promise that resolves to the updated switch step
	 */
	const removeCaseFromSwitch = useCallback(
		async (switchStepId: string, caseId: string) => {
			return removeCaseMutation.mutateAsync({
				templateId: automationTemplateId,
				stepId: switchStepId,
				caseId,
				impersonationContext,
			});
		},
		[automationTemplateId, impersonationContext, removeCaseMutation]
	);

	/**
	 * Updates an existing case in a switch step.
	 *
	 * @param switchStepId - The ID of the switch step containing the case
	 * @param caseId - The ID of the case to update
	 * @param caseData - The updated case data (filter and/or isDefault flag)
	 * @returns Promise that resolves to the updated switch step
	 */
	const updateCase = useCallback(
		async (switchStepId: string, caseId: string, caseData: Api.IAutomationStepCaseStatement) => {
			return updateCaseMutation.mutateAsync({
				templateId: automationTemplateId,
				stepId: switchStepId,
				caseId,
				caseData,
				impersonationContext,
			});
		},
		[automationTemplateId, impersonationContext, updateCaseMutation]
	);

	return {
		addSegment,
		addCaseToSwitch,
		removeCaseFromSwitch,
		updateCase,
		isBusy,
	};
}
